import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, Layers, Settings, Package, BarChart2, Check, X, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { API, apiAssetUrl } from '@/api';
import ProductTypeManager from './ProductTypeManager';
import ProductForm from './ProductForm';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker, getLocalizedField } from '../../utils/localizedContent';


export default function AdminProducts({ products, boosters, onRefresh, lang, setMessage }) {
  const [activeTab, setActiveTab] = useState('products'); // 'products' | 'stock'
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [productTypes, setProductTypes] = useState([]);
  const [showTypeManager, setShowTypeManager] = useState(false);
  const [imagePreview, setImagePreview] = useState([]);
  const [stockEdits, setStockEdits] = useState({});
  const [savingStock, setSavingStock] = useState(null);
  const [search, setSearch] = useState('');

  const l = createTextPicker(lang);
  const filteredProducts = search.trim()
    ? products.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()) || p.name_es?.toLowerCase().includes(search.toLowerCase()))
    : products;

  const [form, setForm] = useState({
    name: '', name_es: '', category: 'board_game', description: '', description_es: '',
    price: '', stock: '0', is_rentable: false, available_for_rent: 0, tags: '', booster_id: '',
    min_players: '', max_players: '', duration_minutes: '',
    images: [], imageUrl: ''
  });

  useEffect(() => { fetchProductTypes(); }, []);

  const fetchProductTypes = async () => {
    try {
      const res = await axios.get(`${API}/product-types`);
      setProductTypes(res.data.types || []);
    } catch (err) {
      console.error('Error fetching product types:', err);
      setProductTypes([
        { type_id: 'board_game', name: 'Board Game', name_es: 'Juego de Mesa', icon: 'dice' },
        { type_id: 'tcg', name: 'TCG', name_es: 'TCG', icon: 'cards' },
        { type_id: 'accessory', name: 'Accessory', name_es: 'Accesorio', icon: 'package' }
      ]);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', name_es: '', category: productTypes[0]?.type_id || 'board_game',
      description: '', description_es: '',
      price: '', stock: '0', is_rentable: false, available_for_rent: 0, tags: '', booster_id: '',
      min_players: '', max_players: '', duration_minutes: '',
      images: [], imageUrl: '', is_pre_order: false, max_pre_orders: ''
    });
    setImagePreview([]);
    setEditing(null);
  };

  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!API) {
      setMessage(l(
        'Falta la URL del backend (REACT_APP_BACKEND_URL). Reconstruye el frontend con esa variable.',
        'Backend URL is missing (REACT_APP_BACKEND_URL). Rebuild the frontend with it set.',
      ));
      return;
    }
    setFieldErrors({});
    const data = {
      ...form,
      min_players: form.category === 'board_game' && form.min_players !== '' ? parseInt(form.min_players, 10) || null : null,
      max_players: form.category === 'board_game' && form.max_players !== '' ? parseInt(form.max_players, 10) || null : null,
      duration_minutes: form.category === 'board_game' && form.duration_minutes !== '' ? parseInt(form.duration_minutes, 10) || null : null,
      price: parseFloat(form.price) || 0,
      stock: 0, // El stock se gestiona aparte en la pestaña Stock
      available_for_rent: 0,
      is_pre_order: false,
      max_pre_orders: null,
      is_rentable: form.category === 'board_game' ? !!form.is_rentable : false,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      booster_id: form.booster_id || null,
      images: []
    };
    delete data.imageUrl;

    try {
      let productId = editing;
      if (editing) {
        data.images = imagePreview.filter(img => !img.isLocal).map(img => img.url || img.preview);
        await axios.put(`${API}/products/${editing}`, data);
        setMessage(l('Producto actualizado', 'Product updated'));
      } else {
        data.images = imagePreview.filter(img => !img.isLocal).map(img => img.url || img.preview);
        const res = await axios.post(`${API}/products`, data);
        productId = res.data.product_id;
        setMessage(l('Producto creado', 'Product created'));
      }

      for (const img of imagePreview.filter(img => img.isLocal)) {
        try { await axios.post(`${API}/products/${productId}/images`, { image: img.preview }); }
        catch (uploadErr) { console.error('Image upload error:', uploadErr); }
      }

      setShowForm(false);
      resetForm();
      setFieldErrors({});
      onRefresh();
    } catch (err) {
      // Extraer errores por campo de la respuesta 422 de Pydantic
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errors = {};
        detail.forEach(e => {
          if (e?.loc?.length >= 2) {
            const field = e.loc[e.loc.length - 1];
            errors[field] = e.msg || 'Invalid value';
          }
        });
        setFieldErrors(errors);
      }
      setMessage(formatApiError(err));
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm(l('Eliminar producto?', 'Delete product?'))) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      setMessage(l('Producto eliminado', 'Product deleted'));
      onRefresh();
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const handleEdit = (product) => {
    setForm({
      name: product.name, name_es: product.name_es || '', category: product.category,
      description: product.description, description_es: product.description_es || '',
      price: product.price, stock: product.stock, is_rentable: product.is_rentable,
      available_for_rent: product.available_for_rent,
      min_players: product.min_players ?? '',
      max_players: product.max_players ?? '',
      duration_minutes: product.duration_minutes ?? '',
      tags: (product.tags || []).join(', '), booster_id: product.booster_id || '',
      images: product.images || [], imageUrl: '',
      is_pre_order: !!product.is_pre_order,
      max_pre_orders: product.max_pre_orders || ''
    });
    setImagePreview((product.images || []).map((url, idx) => ({
      id: `existing_${idx}`,
      preview: apiAssetUrl(url),
      url, isLocal: false
    })));
    setEditing(product.product_id);
    setShowForm(true);
  };

  const handleSaveStock = async (productId) => {
    const edits = stockEdits[productId];
    if (!edits) return;
    setSavingStock(productId);
    try {
      await axios.put(`${API}/products/${productId}`, {
        stock: edits.is_pre_order ? 0 : (parseInt(edits.stock) || 0),
        is_pre_order: !!edits.is_pre_order,
        max_pre_orders: edits.max_pre_orders !== '' ? parseInt(edits.max_pre_orders) || null : null,
        is_rentable: !!edits.is_rentable,
        available_for_rent: parseInt(edits.available_for_rent) || 0,
      });
      setMessage(l('Disponibilidad actualizada', 'Availability updated'));
      const newEdits = { ...stockEdits };
      delete newEdits[productId];
      setStockEdits(newEdits);
      onRefresh();
    } catch (err) {
      setMessage(formatApiError(err));
    }
    setSavingStock(null);
  };

  const getStockVal = (p, field) => {
    if (stockEdits[p.product_id]) return stockEdits[p.product_id][field];
    if (field === 'stock') return p.stock ?? 0;
    if (field === 'is_pre_order') return !!p.is_pre_order;
    if (field === 'max_pre_orders') return p.max_pre_orders ?? '';
    if (field === 'is_rentable') return !!p.is_rentable;
    if (field === 'available_for_rent') return p.available_for_rent ?? 0;
    return '';
  };

  const setStockVal = (productId, field, value) => {
    setStockEdits(prev => ({
      ...prev,
      [productId]: { ...( prev[productId] || {}), [field]: value }
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-xl">{l('Productos', 'Products')} ({products.length})</h2>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={l('Buscar...', 'Search...')}
            className="bg-[#12121A] border border-white/10 text-white rounded-xl px-4 py-2 text-sm outline-none focus:border-[#C9A962] w-40"
          />
          <button onClick={() => setShowTypeManager(!showTypeManager)}
            className="px-4 py-2 text-sm flex items-center gap-2 bg-[#1E1E2A] border border-white/10 rounded-xl text-[#A1A1AA] hover:text-white hover:border-[#C9A962] transition-colors"
            data-testid="manage-types-btn">
            <Settings size={16} />{l('Tipos', 'Types')}
          </button>
          {activeTab === 'products' && (
            <button onClick={() => { setShowForm(true); resetForm(); }}
              className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
              data-testid="new-product-btn">
              <Plus size={16} />{l('Nuevo Producto', 'New Product')}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b border-white/5 pb-3">
        <button onClick={() => setActiveTab('products')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeTab === 'products' ? 'bg-[#C9A962] text-[#0A1612]' : 'bg-[#12121A] text-[#A1A1AA] hover:text-white'}`}>
          <Package size={15} />{l('Productos', 'Products')}
        </button>
        <button onClick={() => setActiveTab('stock')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeTab === 'stock' ? 'bg-[#C9A962] text-[#0A1612]' : 'bg-[#12121A] text-[#A1A1AA] hover:text-white'}`}>
          <BarChart2 size={15} />{l('Stock y Disponibilidad', 'Stock & Availability')}
        </button>
      </div>

      {showTypeManager && (
        <ProductTypeManager productTypes={productTypes} onRefresh={fetchProductTypes} lang={lang} setMessage={setMessage} onClose={() => setShowTypeManager(false)} />
      )}

      {/* ── TAB: Products ── */}
      {activeTab === 'products' && (
        <>
          {showForm && (
            <ProductForm form={form} setForm={setForm} editing={editing} productTypes={productTypes} boosters={boosters} imagePreview={imagePreview} setImagePreview={setImagePreview} onSubmit={handleSubmit} onClose={() => { setShowForm(false); setFieldErrors({}); }} lang={lang} setMessage={setMessage} fieldErrors={fieldErrors} />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProducts.map(p => {
              const linkedBooster = p.booster_id ? boosters.find(b => b.booster_id === p.booster_id) : null;
              const productType = productTypes.find(t => t.type_id === p.category);
              const imageUrl = apiAssetUrl(p.images?.[0]);
              return (
                <div key={p.product_id} className="bg-[#12121A] border border-white/5 rounded-xl p-4 flex items-center gap-3" data-testid={`admin-product-${p.product_id}`}>
                  <img src={imageUrl || 'https://placehold.co/100x100/1E1E2A/A1A1AA?text=No+Image'} alt={p.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-white/5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{p.name}</p>
                    <p className="text-[#10B981] font-bold text-sm">{p.price?.toFixed(2)}€</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {productType && <span className="text-[#C9A962] text-xs">{getLocalizedField(productType, 'name', lang, productType.name)}</span>}
                      {linkedBooster && <span className="text-[#C9A962] text-xs flex items-center gap-1"><Layers size={10} /> {linkedBooster.name}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <a href={`/store/${p.product_id}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 rounded-lg text-[#3B82F6] hover:bg-blue-500/10"
                      title={l('Ver en la tienda', 'View in store')}
                      onClick={e => e.stopPropagation()}>
                      <ExternalLink size={14} />
                    </a>
                    <button onClick={() => handleEdit(p)} className="p-1.5 rounded-lg text-[#A1A1AA] hover:text-white hover:bg-white/5" data-testid={`edit-product-${p.product_id}`}><Edit3 size={14} /></button>
                    <button onClick={() => handleDelete(p.product_id)} className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-500/10" data-testid={`delete-product-${p.product_id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── TAB: Stock & Disponibilidad ── */}
      {activeTab === 'stock' && (
        <div className="space-y-2">
          {products.length === 0 && (
            <p className="text-[#A1A1AA] text-center py-8 text-sm">{l('No hay productos. Créalos en la pestaña Productos.', 'No products. Create them in the Products tab.')}</p>
          )}
          {filteredProducts.map(p => {
            const isEditing = !!stockEdits[p.product_id];
            const isPre = getStockVal(p, 'is_pre_order');
            const isRent = getStockVal(p, 'is_rentable');
            const iclsBase = "bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-1.5 text-sm w-20 focus:outline-none focus:border-[#C9A962]";
            return (
              <div key={p.product_id} className="bg-[#12121A] border border-white/5 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[140px]">
                  <p className="text-white font-semibold text-sm">{p.name}</p>
                  <p className="text-[#52525B] text-xs">{p.price?.toFixed(2)}€</p>
                </div>

                {/* Stock */}
                <div className="flex flex-col items-center gap-1">
                  <label className="text-[#52525B] text-[10px] uppercase">{l('Stock', 'Stock')}</label>
                  <input type="number" min="0" disabled={isPre}
                    value={getStockVal(p, 'stock')}
                    onChange={e => setStockVal(p.product_id, 'stock', e.target.value)}
                    className={`${iclsBase} ${isPre ? 'opacity-40 cursor-not-allowed' : ''}`} />
                </div>

                {/* Pre-order */}
                <div className="flex flex-col items-center gap-1">
                  <label className="text-[#52525B] text-[10px] uppercase">{l('Reserva', 'Pre-order')}</label>
                  <input type="checkbox" checked={isPre}
                    onChange={e => setStockVal(p.product_id, 'is_pre_order', e.target.checked)}
                    className="w-4 h-4 accent-[#C9A962]" />
                </div>

                {/* Max pre-orders — shown only if pre-order active */}
                {isPre && (
                  <div className="flex flex-col items-center gap-1">
                    <label className="text-[#52525B] text-[10px] uppercase">{l('Máx. Reservas', 'Max Orders')}</label>
                    <input type="number" min="1" placeholder={l('∞', '∞')}
                      value={getStockVal(p, 'max_pre_orders')}
                      onChange={e => setStockVal(p.product_id, 'max_pre_orders', e.target.value)}
                      className={iclsBase} />
                  </div>
                )}

                {/* Rentable */}
                <div className="flex flex-col items-center gap-1">
                  <label className="text-[#52525B] text-[10px] uppercase">{l('Alquiler', 'Rental')}</label>
                  <input type="checkbox" checked={isRent}
                    onChange={e => setStockVal(p.product_id, 'is_rentable', e.target.checked)}
                    className="w-4 h-4 accent-[#10B981]" />
                </div>

                {/* Available for rent */}
                {isRent && (
                  <div className="flex flex-col items-center gap-1">
                    <label className="text-[#52525B] text-[10px] uppercase">{l('Uds. Alquiler', 'Rent Qty')}</label>
                    <input type="number" min="0"
                      value={getStockVal(p, 'available_for_rent')}
                      onChange={e => setStockVal(p.product_id, 'available_for_rent', e.target.value)}
                      className={iclsBase} />
                  </div>
                )}

                {/* Save / saved indicator */}
                {isEditing ? (
                  <button onClick={() => handleSaveStock(p.product_id)} disabled={savingStock === p.product_id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C9A962] text-[#0A1612] rounded-lg text-xs font-semibold hover:bg-[#B8984E] disabled:opacity-60">
                    <Check size={13} />{savingStock === p.product_id ? '...' : l('Guardar', 'Save')}
                  </button>
                ) : (
                  <span className="text-[#52525B] text-xs">
                    {p.is_pre_order ? '🔒 ' + l('Reserva', 'Pre-order') : `📦 ${p.stock ?? 0}`}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
